import { NavLink } from 'react-router-dom'
import { X, Home, User, Users, MessageCircle } from 'lucide-react'
import { NavigationItem } from '@/types'
import { useEffect } from 'react'

interface MobileSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const navigationItems: NavigationItem[] = [
  {
    id: 'home',
    label: 'Dashboard',
    icon: 'Home',
    path: '/',
  },
  {
    id: 'avatar',
    label: 'Avatar',
    icon: 'User',
    path: '/avatar',
  },
  {
    id: 'connections',
    label: 'Connections',
    icon: 'Users',
    path: '/connections',
  },
  {
    id: 'chat',
    label: 'Messages',
    icon: 'MessageCircle',
    path: '/chat',
  },
]

const iconMap = {
  Home,
  User,
  Users,
  MessageCircle,
}

export function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  return (
    <>
      {/* Enhanced Backdrop with blur and smooth transition */}
      <div
        role="button"
        tabIndex={0}
        className={`fixed inset-0 z-40 backdrop-blur-sm bg-black/50 transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClose()
          }
        }}
        aria-label="Close sidebar"
        style={{
          // Add safe area support
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
        }}
      />

      {/* Enhanced Sidebar with smooth animations */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl transform transition-all duration-300 ease-out md:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          // Add safe area support for notched devices
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-secondary-200 bg-white">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-8 h-8 bg-primary-600 rounded-lg">
              <span className="text-white font-bold text-sm">S</span>
            </div>
            <span className="ml-3 text-lg font-semibold text-secondary-900">
              useSelft
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 active:bg-secondary-200 transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-6 space-y-1 flex-1 overflow-y-auto">
          {navigationItems.map((item) => {
            const IconComponent = iconMap[item.icon as keyof typeof iconMap]

            return (
              <NavLink
                key={item.id}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-xl transition-all duration-200 touch-manipulation min-h-[48px] relative ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 shadow-sm border border-primary-200'
                      : 'text-secondary-700 hover:bg-secondary-50 active:bg-secondary-100 hover:text-secondary-900'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                    )}

                    <div className={`p-2 rounded-lg mr-3 transition-colors ${
                      isActive
                        ? 'bg-primary-100'
                        : 'group-hover:bg-secondary-100'
                    }`}>
                      <IconComponent className="h-5 w-5" />
                    </div>

                    <span className="text-base font-medium flex-1">{item.label}</span>

                    {/* Touch feedback ripple effect */}
                    <div className="absolute inset-0 rounded-xl bg-black/5 opacity-0 active:opacity-100 transition-opacity pointer-events-none" />
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Footer */}
        <div
          className="px-4 py-4 border-t border-secondary-200 bg-white"
          style={{
            paddingBottom: `calc(1rem + env(safe-area-inset-bottom))`,
          }}
        >
          <div className="text-xs text-secondary-500 text-center">
            <div className="mb-1 font-medium">useSelft Standalone</div>
            <div className="text-secondary-400">Version 1.0.0</div>
          </div>
        </div>
      </div>
    </>
  )
}